import { destructureImpactScore } from '@src/utility/impactScore';
import React, { useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { convertTimeToMMSS } from '../../utility/timeFormat';

import type {
  ClipFragment,
  GenerationContext,
} from '../../services/AIClipProducer';

type SelectionSummaryBarProps = {
  /**
   * The picks, already resolved against the results on screen — so the count
   * here is the count the numbers on the cards and the saved reel agree on
   * (PLAT-11093).
   */
  selectedClips: ClipFragment[];
  generationContext?: GenerationContext;
};

export const SelectionSummaryBar = ({
  selectedClips,
  generationContext,
}: SelectionSummaryBarProps) => {
  const selectionSummary = useMemo(() => {
    const selected = selectedClips;
    const totalDuration = selected.reduce(
      (sum, f) => sum + (f.duration || 0),
      0,
    );
    const scores = selected
      .map(f => destructureImpactScore(f.impactScore).main)
      .filter(s => s > 0);
    const avgScore =
      scores.length > 0
        ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
        : 0;
    const themes = [
      ...new Set(
        selected.map(f => f.themeDisplayName || f.theme).filter(Boolean),
      ),
    ];
    const speakers = [
      ...new Set(selected.map(f => f.speakerName).filter(Boolean)),
    ];
    return {
      count: selected.length,
      totalDuration,
      avgScore,
      themes,
      speakers,
    };
  }, [selectedClips]);

  const contextTags = useMemo(() => {
    if (!generationContext) return [];
    const tags: { label: string; value: string; overflow?: string[] }[] = [];
    if (generationContext.themes?.length) {
      tags.push({
        label: 'Prompt',
        value: generationContext.themes.join(', '),
      });
    }
    if (generationContext.suggestedTheme) {
      tags.push({ label: 'Topic', value: generationContext.suggestedTheme });
    }
    if (generationContext.customPrompt) {
      tags.push({ label: 'Custom', value: generationContext.customPrompt });
    }
    if (generationContext.duration) {
      tags.push({
        label: 'Duration',
        value: `~${generationContext.duration}s`,
      });
    }
    if (generationContext.speakers?.length) {
      const speakers = generationContext.speakers;
      if (speakers.length <= 2) {
        tags.push({ label: 'Speakers', value: speakers.join(', ') });
      } else {
        tags.push({
          label: 'Speakers',
          value: `${speakers.slice(0, 2).join(', ')}`,
          overflow: speakers.slice(2),
        });
      }
    }
    if (generationContext.goal) {
      tags.push({ label: 'Goal', value: generationContext.goal });
    }
    if (generationContext.audiences?.length) {
      const audiences = generationContext.audiences;
      if (audiences.length <= 2) {
        tags.push({ label: 'Audience', value: audiences.join(', ') });
      } else {
        tags.push({
          label: 'Audience',
          value: `${audiences.slice(0, 2).join(', ')}`,
          overflow: audiences.slice(2),
        });
      }
    }
    return tags;
  }, [generationContext]);

  return (
    <Bar data-testid="clip-results-selection-bar">
      {contextTags.map((tag, i) => (
        <React.Fragment key={tag.label}>
          <SummaryItem>
            <SummaryLabel>{tag.label}</SummaryLabel>
            <SummaryValue>
              {tag.value}
              {tag.overflow && (
                <>
                  {' '}
                  <OverflowHint
                    data-tooltip-id={`summary-overflow-${tag.label}`}
                  >
                    +{tag.overflow.length} more
                  </OverflowHint>
                  <Tooltip
                    id={`summary-overflow-${tag.label}`}
                    place="bottom"
                    noArrow
                    opacity={1}
                    style={{
                      maxWidth: '420px',
                      backgroundColor: 'var(--popover)',
                      color: 'var(--popover-foreground)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '6px',
                      boxShadow: 'var(--elevation-sm)',
                      padding: '5px 10px',
                      zIndex: '1000003',
                      lineHeight: '1.4',
                    }}
                    content={tag.overflow.join(', ')}
                  />
                </>
              )}
            </SummaryValue>
          </SummaryItem>
          {i < contextTags.length - 1 && <SummaryDivider />}
        </React.Fragment>
      ))}
      {contextTags.length > 0 && <SummaryDivider />}
      <SummaryItem>
        <SummaryLabel>Selected</SummaryLabel>
        <SummaryValue>
          {selectionSummary.count > 0
            ? `${selectionSummary.count} clip${selectionSummary.count !== 1 ? 's' : ''}`
            : '0'}
        </SummaryValue>
      </SummaryItem>
      <SummaryDivider />
      <SummaryItem>
        <SummaryLabel>Total Duration</SummaryLabel>
        <SummaryValue>
          {convertTimeToMMSS(Math.round(selectionSummary.totalDuration))}
        </SummaryValue>
      </SummaryItem>
      <SummaryDivider />
      <SummaryItem>
        <SummaryLabel>Avg. Impact</SummaryLabel>
        {selectionSummary.avgScore > 0 ? (
          <SummaryHighlight>{selectionSummary.avgScore}</SummaryHighlight>
        ) : (
          <SummaryValue>–</SummaryValue>
        )}
      </SummaryItem>
    </Bar>
  );
};

const Bar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  margin-top: 8px;
  padding: 6px 0 6px 8px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const SummaryLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
`;

const SummaryValue = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const SummaryHighlight = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  color: var(--primary-hover);
`;

const OverflowHint = styled.span`
  color: var(--text-muted);
  cursor: default;
  &:hover {
    color: var(--foreground);
  }
`;

const SummaryDivider = styled.div`
  width: 1px;
  height: 16px;
  background-color: var(--border);
  flex-shrink: 0;
`;
